import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaMoneyBillWave, FaShoppingCart, FaLeaf, FaSeedling } from 'react-icons/fa';
import ReactPixel from 'react-facebook-pixel';
import { v4 as uuidv4 } from 'uuid';
import sha256 from 'crypto-js/sha256';
import ReactGA from "react-ga4";
import { getUTMHistory } from '../hooks/useUTM';

// ── Organic Food Color Palette ──────────────────────────────────────────────
// Russian Red:    #CD0302
// Sunburst:       #F0C400
// Edam:           #F5D19D
// Citrusy Orange: #F88702
// Autumn Green:   #4F7E17
// ────────────────────────────────────────────────────────────────────────────

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400;700&display=swap');

  :root {
    --russian-red:    #CD0302;
    --sunburst:       #F0C400;
    --edam:           #F5D19D;
    --citrusy:        #F88702;
    --autumn-green:   #4F7E17;
    --autumn-light:   #6aad1f;
    --edam-light:     #fdf3e3;
    --edam-dark:      #e8c070;
    --soil:           #3d2b1f;
    --cream:          #fffdf5;
    --fog:            #f7f2ea;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body, .checkout-root {
    font-family: 'Lato', sans-serif;
    background: var(--cream);
    color: var(--soil);
  }

  .checkout-root {
    min-height: 100vh;
    background:
      radial-gradient(ellipse at 0% 0%, rgba(79,126,23,0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 100% 100%, rgba(205,3,2,0.06) 0%, transparent 50%),
      var(--cream);
  }

  /* ── Typography ── */
  .organic-heading {
    font-family: 'Playfair Display', Georgia, serif;
    color: var(--autumn-green);
    font-style: italic;
  }

  .section-label {
    font-family: 'Lato', sans-serif;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--citrusy);
  }

  /* ── Leaf Decoration ── */
  .leaf-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--autumn-green);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 20px;
  }

  /* ── Card ── */
  .organic-card {
    background: #fff;
    border-radius: 18px;
    border: 1.5px solid var(--edam);
    box-shadow: 0 2px 20px rgba(79,126,23,0.07), 0 1px 4px rgba(0,0,0,0.04);
    padding: 28px;
    position: relative;
    overflow: hidden;
  }

  .organic-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--autumn-green), var(--citrusy), var(--sunburst));
    border-radius: 18px 18px 0 0;
  }

  .organic-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 22px;
    padding-bottom: 16px;
    border-bottom: 1px dashed var(--edam-dark);
  }

  .step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--autumn-green);
    color: #fff;
    font-family: 'Playfair Display', serif;
    font-size: 0.95rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Inputs ── */
  .organic-input,
  .organic-select,
  .organic-textarea {
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid var(--edam-dark);
    border-radius: 10px;
    background: var(--fog);
    font-family: 'Lato', sans-serif;
    font-size: 0.92rem;
    color: var(--soil);
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    outline: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
  }

  .organic-input:focus,
  .organic-select:focus,
  .organic-textarea:focus {
    border-color: var(--autumn-green);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(79,126,23,0.12);
  }

  .organic-textarea { resize: none; }

  .organic-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--soil);
    margin-bottom: 6px;
    letter-spacing: 0.02em;
  }

  .field-group { display: flex; flex-direction: column; gap: 4px; }

  /* ── Radio Cards ── */
  .radio-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border: 2px solid var(--edam-dark);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s, transform 0.15s;
    background: var(--fog);
    user-select: none;
  }

  .radio-card:hover {
    border-color: var(--citrusy);
    transform: translateY(-1px);
  }

  .radio-card.active {
    border-color: var(--autumn-green);
    background: rgba(79,126,23,0.06);
    box-shadow: 0 0 0 3px rgba(79,126,23,0.1);
  }

  .radio-card input[type="radio"] { accent-color: var(--autumn-green); }

  .radio-label { display: flex; align-items: center; gap: 10px; font-weight: 600; font-size: 0.92rem; }

  /* ── Buttons ── */
  .btn-primary {
    width: 100%;
    padding: 16px 24px;
    background: linear-gradient(135deg, var(--autumn-green) 0%, var(--autumn-light) 100%);
    color: #fff;
    border: none;
    border-radius: 14px;
    font-family: 'Playfair Display', serif;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
    box-shadow: 0 4px 18px rgba(79,126,23,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(79,126,23,0.4);
    filter: brightness(1.05);
  }

  .btn-primary:disabled {
    background: linear-gradient(135deg, #c8c8c8, #b0b0b0);
    color: #777;
    cursor: not-allowed;
    box-shadow: none;
  }

  .btn-secondary {
    padding: 10px 20px;
    background: linear-gradient(135deg, var(--citrusy) 0%, var(--russian-red) 100%);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.88rem;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 3px 12px rgba(248,135,2,0.35);
    white-space: nowrap;
  }

  .btn-secondary:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 18px rgba(248,135,2,0.45);
  }

  .btn-modal {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, var(--citrusy) 0%, var(--russian-red) 100%);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-family: 'Playfair Display', serif;
    font-size: 1.05rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 16px rgba(205,3,2,0.3);
  }

  .btn-modal:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 22px rgba(205,3,2,0.4);
  }

  /* ── Qty Controls ── */
  .qty-control {
    display: flex;
    align-items: center;
    gap: 0;
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    border-radius: 8px;
    overflow: hidden;
  }

  .qty-btn {
    background: none;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    font-size: 1rem;
    color: var(--autumn-green);
    font-weight: 700;
    transition: background 0.15s;
  }

  .qty-btn:hover:not(:disabled) { background: var(--edam); }
  .qty-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .qty-num { padding: 4px 8px; font-weight: 700; font-size: 0.88rem; min-width: 28px; text-align: center; }

  /* ── Remove Btn ── */
  .remove-btn {
    background: none;
    border: none;
    color: var(--russian-red);
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: color 0.15s;
  }

  .remove-btn:hover { color: #8a0201; }

  /* ── Floating Cart ── */
  .floating-cart {
    position: fixed;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    background: linear-gradient(160deg, var(--autumn-green) 0%, #3a6012 100%);
    box-shadow: -4px 0 24px rgba(79,126,23,0.35);
    border-radius: 18px 0 0 18px;
    padding: 16px 14px 16px 18px;
    z-index: 50;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 80px;
    border: 1.5px solid rgba(255,255,255,0.2);
    border-right: none;
  }

  .floating-cart-icon { font-size: 1.5rem; color: var(--sunburst); }
  .floating-cart-label { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.8); }
  .floating-cart-amount { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 700; color: #fff; text-align: center; line-height: 1.2; }

  /* ── Order Summary Sidebar ── */
  .summary-card {
    background: #fff;
    border-radius: 18px;
    border: 1.5px solid var(--edam);
    box-shadow: 0 4px 28px rgba(79,126,23,0.1);
    padding: 24px;
    position: sticky;
    top: 88px;
    overflow: hidden;
  }

  .summary-card::after {
    content: '';
    position: absolute;
    bottom: -30px; right: -30px;
    width: 120px; height: 120px;
    background: radial-gradient(circle, rgba(79,126,23,0.08) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  .summary-item {
    display: flex;
    gap: 12px;
    padding-bottom: 16px;
    border-bottom: 1px dashed var(--edam-dark);
    margin-bottom: 16px;
  }

  .summary-item:last-child { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }

  .summary-item img {
    width: 60px; height: 60px;
    object-fit: cover;
    border-radius: 10px;
    border: 1.5px solid var(--edam);
  }

  .price-row { display: flex; justify-content: space-between; align-items: center; font-size: 0.88rem; }
  .price-row + .price-row { margin-top: 8px; }
  .price-label { color: #777; }
  .price-val { font-weight: 700; }
  .price-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 2px solid var(--edam-dark);
  }

  .price-total-label { font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 700; }
  .price-total-val { font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 700; color: var(--russian-red); }

  .savings-strip {
    background: linear-gradient(90deg, rgba(79,126,23,0.1), rgba(240,196,0,0.15));
    border: 1px solid rgba(79,126,23,0.2);
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
  }

  /* ── Coupon ── */
  .coupon-row { display: flex; gap: 8px; }
  .coupon-row .organic-input { flex: 1; }

  /* ── Payment confirmed ── */
  .payment-confirmed {
    background: rgba(79,126,23,0.08);
    border: 1.5px solid rgba(79,126,23,0.25);
    border-radius: 10px;
    padding: 12px 14px;
    margin-top: 14px;
  }

  /* ── Alerts ── */
  .alert-error {
    background: rgba(205,3,2,0.06);
    border: 1.5px solid rgba(205,3,2,0.2);
    border-radius: 10px;
    padding: 12px 16px;
    color: var(--russian-red);
    font-size: 0.88rem;
    font-weight: 600;
  }

  /* ── Modal ── */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(61,43,31,0.55);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 60;
    padding: 16px;
  }

  .modal-box {
    background: #fff;
    border-radius: 20px;
    padding: 32px 28px;
    max-width: 420px;
    width: 100%;
    position: relative;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    border: 1.5px solid var(--edam);
  }

  .modal-box::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--citrusy), var(--russian-red));
    border-radius: 20px 20px 0 0;
  }

  .modal-close {
    position: absolute;
    top: 16px; right: 16px;
    background: var(--fog);
    border: none;
    border-radius: 50%;
    width: 32px; height: 32px;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #777;
    transition: background 0.15s;
  }

  .modal-close:hover { background: var(--edam); }

  .agent-number-box {
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    border-radius: 10px;
    padding: 14px 16px;
    margin: 12px 0 18px;
    text-align: center;
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--soil);
    letter-spacing: 0.05em;
  }

  /* ── Loading Overlay ── */
  .loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(61,43,31,0.35);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 55;
  }

  .loading-box {
    background: #fff;
    border-radius: 16px;
    padding: 20px 28px;
    display: flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    border: 1.5px solid var(--edam);
  }

  /* ── Grid ── */
  .checkout-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 20px;
  }

  @media (min-width: 1024px) {
    .checkout-grid { grid-template-columns: 1fr 380px; }
  }

  .form-stack { display: flex; flex-direction: column; gap: 20px; }

  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  @media (max-width: 560px) { .two-col { grid-template-columns: 1fr; } }

  /* ── Spinner ── */
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 22px; height: 22px;
    border: 3px solid rgba(255,255,255,0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }

  .spinner-green {
    width: 20px; height: 20px;
    border: 3px solid rgba(79,126,23,0.2);
    border-top-color: var(--autumn-green);
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }

  /* ── Page Header ── */
  .checkout-header {
    background: linear-gradient(135deg, var(--autumn-green) 0%, #3a6012 100%);
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 14px;
    border-bottom: 4px solid var(--sunburst);
  }

  .checkout-header-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    font-style: italic;
  }

  .checkout-header-sub {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.75);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  /* ── Tag ── */
  .tag-free {
    display: inline-block;
    background: var(--autumn-green);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    letter-spacing: 0.05em;
  }

  /* ── Select wrapper ── */
  .select-wrap { position: relative; }
  .select-wrap::after {
    content: '▾';
    position: absolute;
    right: 12px; top: 50%;
    transform: translateY(-50%);
    color: var(--autumn-green);
    font-size: 0.8rem;
    pointer-events: none;
  }
`;

function CheckoutPage({ setCart, addOrder }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getAuthHeaders, fetchUserInfo, login } = useAuth();
  const eventId = uuidv4();

  const cartItems = location.state?.cartItems || [];
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', notes: '' });
  const nameInputTimer = useRef(null);
  const phoneInputTimer = useRef(null);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bkashTrxId, setBkashTrxId] = useState('');
  const [bkashPhoneNo, setBkashPhoneNo] = useState('');
  const [paymentModalError, setPaymentModalError] = useState('');
  const [paymentDetailsConfirmed, setPaymentDetailsConfirmed] = useState(false);
  const [mainFormError, setMainFormError] = useState('');

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [isOrderPlacing, setIsOrderPlacing] = useState(false);

  const [deliveryOption, setDeliveryOption] = useState('');

  const [selectedDivisionName, setSelectedDivisionName] = useState('');
  const [selectedDistrictName, setSelectedDistrictName] = useState('');
  const [selectedUpazilaName, setSelectedUpazilaName] = useState('');
  const [selectedUnionName, setSelectedUnionName] = useState('');

  const [isValid, setIsValid] = useState(false);
  const [message, setMessage] = useState('');
  const [addressMessage, setAddressMessage] = useState('Enter your address or click auto-fill');

  const bKashAgentNumber = '017XXXXXXXX';
  const nagadAgentNumber = '018XXXXXXXX';

  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [districts, setDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [upazilas, setUpazilas] = useState([]);
  const [selectedUpazilaId, setSelectedUpazilaId] = useState('');
  const [unions, setUnions] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://bdapi.vercel.app/api/v.1';

  const [isOrderButtonEnabled, setIsOrderButtonEnabled] = useState(false);
  const [orderId] = useState(uuidv4());
  const clientId = localStorage.getItem('client_id');
  const hasFiredRef = useRef(false);

  function getFbClickId() {
    const match = document.cookie.match(/_fbc=([^;]*)/);
    return match ? match[1] : null;
  }

  function getFbBrowserId() {
    const match = document.cookie.match(/_fbp=([^;]*)/);
    return match ? match[1] : null;
  }

  useEffect(() => {
    if ((selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && !paymentDetailsConfirmed) {
      setShowPaymentModal(true);
    } else {
      setShowPaymentModal(false);
    }
    if (selectedPaymentMethod === 'cashOnDelivery') {
      setPaymentDetailsConfirmed(true);
      setBkashTrxId('');
      setBkashPhoneNo('');
    } else if (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') {
      if (!paymentDetailsConfirmed) setPaymentDetailsConfirmed(false);
    }
  }, [selectedPaymentMethod, paymentDetailsConfirmed]);

  useEffect(() => {
    const isFormFilled = formData.name.trim() !== '' && formData.phone.trim() !== '' && formData.address.trim() !== '';
    const isPaymentSelected = selectedPaymentMethod !== '';
    const isDeliveryOptionSelected = deliveryOption !== '';
    let isPaymentConfirmed = false;
    if (selectedPaymentMethod === 'cashOnDelivery') isPaymentConfirmed = true;
    else if (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') isPaymentConfirmed = paymentDetailsConfirmed;
    setIsOrderButtonEnabled(isFormFilled && isPaymentSelected && isDeliveryOptionSelected && isPaymentConfirmed && cartItems.length > 0 && selectedDivisionId.length > 0 && selectedDistrictId.length > 0 && isValid);
  }, [formData, selectedPaymentMethod, paymentDetailsConfirmed, deliveryOption, cartItems, selectedDivisionId, selectedDistrictId, isValid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'name' || name === 'phone') {
      if (name === 'name') {
        clearTimeout(nameInputTimer.current);
      } else {
        clearTimeout(phoneInputTimer.current);
        const checkValidity = (num) => {
          const digitsOnly = num.replace(/[^0-9+]/g, '');
          if (digitsOnly === '') { setIsValid(false); setMessage('Phone number cannot be empty.'); return; }
          if (digitsOnly.startsWith('+88')) {
            const numberWithoutCountryCode = digitsOnly.slice(1);
            if (numberWithoutCountryCode.length === 13) {
              if (/^\d+$/.test(numberWithoutCountryCode)) { setIsValid(true); setMessage('Valid phone number.'); }
              else { setIsValid(false); setMessage('Use digits only.'); }
            } else { setIsValid(false); setMessage('Provide 13 digits including +88.'); }
          } else {
            if (digitsOnly.length === 11) {
              if (/^\d+$/.test(digitsOnly)) { setIsValid(true); setMessage('Valid phone number.'); }
              else { setIsValid(false); setMessage('Use digits only.'); }
            } else { setIsValid(false); setMessage('Provide 11-digit phone number.'); }
          }
        };
        checkValidity(value);
      }
      const timer = setTimeout(() => { sendRealtimeDataToFlask(name, value); }, 500);
      if (name === 'name') nameInputTimer.current = timer;
      else { if (isValid) phoneInputTimer.current = timer; }
    } else if (name === 'address') {
      if (formData.address.trim() === '') setAddressMessage('Enter your address or click auto-fill');
      else setAddressMessage('');
    }
  };

  const sendRealtimeDataToFlask = async (fieldName, fieldValue) => {
    const clientId = localStorage.getItem('client_id');
    const today = new Date();
    const dateOnly = today.toDateString();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/realtime-input', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field: fieldName, value: fieldValue,
          visitPageDate: dateOnly, ServerAPICallingDate: `${day}-${month}-${year}`,
          clientId: clientId,
          cartItems: cartItems.map(item => ({
            productId: item.id, name: item.name, quantity: item.quantity,
            price: item.price, image: item.image, originalPrice: item.originalPrice || item.price,
            selectedSize: item.selectedSize || null, selectedColor: item.selectedColor ? item.selectedColor.name : null,
          })),
        }),
      });
      if (response.ok) console.log(`Real-time data sent for ${fieldName}`);
    } catch (error) { console.error(`Error sending real-time data for ${fieldName}:`, error); }
  };

  const calculateSubtotal = () => cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = calculateSubtotal();
    if (appliedCoupon.discount_type === 'percentage') return (subtotal * appliedCoupon.discount_value) / 100;
    if (appliedCoupon.discount_type === 'fixed') return appliedCoupon.discount_value;
    return 0;
  };
  const calculateTotalPAW = () => cartItems.reduce((acc, item) => acc + (typeof item.PAW === 'number' ? item.PAW : 0) * item.quantity, 0);
  const calculateTotalPCW = () => cartItems.reduce((acc, item) => acc + (typeof item.PCW === 'number' ? item.PCW : 0) * item.quantity, 0);
  const totalPAW = calculateTotalPAW();
  const totalPCW = calculateTotalPCW();
  const calculateTotalRevenue = () => cartItems.reduce((acc, item) => {
    const price = typeof item.price === 'number' ? item.price : 0;
    const buyingPrice = typeof item.buyingPrice === 'number' ? item.buyingPrice : 0;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
    return acc + (price - buyingPrice) * quantity;
  }, 0);
  const totalRevenue = calculateTotalRevenue();

  const getDeliveryChargeInfo = () => {
    let charge = 0; let display = ''; let isFree = false;
    if (totalPCW === 0) { charge = 0; display = 'Free'; isFree = true; }
    else if (totalPCW > 0 && totalPCW <= 1) {
      if (deliveryOption === 'insideDhaka') { charge = 80; display = '৳80'; }
      else if (deliveryOption === 'outsideDhaka') { charge = 130; display = '৳130'; }
      else { charge = 0; display = 'Select option'; }
    } else {
      if (deliveryOption === 'insideDhaka') { charge = Math.round(totalPCW * 80); display = `৳${charge}`; }
      else if (deliveryOption === 'outsideDhaka') { charge = Math.round(totalPCW * 130); display = `৳${charge}`; }
      else { charge = 0; display = 'Select option'; }
    }
    return { chargeForApi: charge, displayCharge: display, isFree };
  };
  const { chargeForApi, displayCharge, isFree } = getDeliveryChargeInfo();

  const calculateACDC = () => {
    if (totalPAW === 0) return 0;
    if (totalPAW <= 0.5) return 50;
    if (totalPAW <= 1) return 75;
    return Math.round(totalPAW * 60);
  };
  const acdcValue = calculateACDC();
  const actualTotalRevenue = totalPCW === 0 ? totalRevenue - acdcValue : totalRevenue;

  const calculateTotal = () => Math.max(0, calculateSubtotal() - calculateDiscount());
  const calculateTotalOriginalPrice = () => cartItems.reduce((acc, item) => acc + (item.originalPrice || item.price) * item.quantity, 0);
  const calculateOverallSavings = () => (calculateTotalOriginalPrice() - calculateSubtotal()) + calculateDiscount();

  const handlePaymentMethodChange = (method) => { setSelectedPaymentMethod(method); setPaymentDetailsConfirmed(false); setMainFormError(''); };
  const handleDeliveryOptionChange = (option) => { setDeliveryOption(option); setMainFormError(''); };

  const handlePaySubmit = () => {
    setPaymentModalError('');
    if (!bkashTrxId.trim()) { setPaymentModalError('TrxID cannot be empty.'); return; }
    if (!/^\d{11}$/.test(bkashPhoneNo)) { setPaymentModalError('Provide valid 11-digit mobile number.'); return; }
    setPaymentDetailsConfirmed(true);
    setShowPaymentModal(false);
  };

  const handleApplyCoupon = async () => {
    setCouponError(''); setCouponSuccess(''); setAppliedCoupon(null);
    if (!couponCode.trim()) { setCouponError('Coupon code cannot be empty.'); return; }
    const clientId = localStorage.getItem('client_id');
    try {
      const response = await fetch(`https://bazar-wala-main-server-974485829900.asia-south1.run.app/apply_coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: couponCode, cart_total: calculateSubtotal(), client_id: clientId }),
      });
      const data = await response.json();
      if (response.ok) { setAppliedCoupon(data.coupon); setCouponSuccess(data.message); }
      else {
        setCouponError(data.error);
        if (response.status === 401) setCouponError('Your session has expired. Please login again.');
        else if (response.status === 429) setCouponError(data.error);
      }
    } catch (error) { console.error('Error applying coupon:', error); setCouponError('Error applying coupon.'); }
  };

  const updateCouponUsage = async (code) => {
    try {
      await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/use_coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: code, client_id: clientId }),
      });
    } catch (error) { console.error('Error updating coupon usage:', error); }
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setMainFormError('');
    if (!isOrderButtonEnabled) { setMainFormError('Please fill all required information.'); return; }
    proceedToOrderSubmission();
  };

  const eventTime = Math.floor(Date.now() / 1000);

  const proceedToOrderSubmission = async () => {
    setIsOrderPlacing(true);
    setMainFormError('');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const utmHistory = getUTMHistory();
    const orderSummary = {
      orderId: `${orderId}`, orderDate: new Date().toLocaleDateString(), orderTime: new Date().toLocaleTimeString(),
      eventTime, customerInfo: formData, division: selectedDivisionName, district: selectedDistrictName,
      upazila: selectedUpazilaName != null ? selectedUpazilaName : "Not selected",
      cartItems: cartItems.map(item => ({
        productId: item.productId, name: item.name, quantity: item.quantity, price: item.price, image: item.image,
        originalPrice: item.originalPrice || item.price, selectedSize: item.selectedSize || null,
        selectedColor: item.selectedColor ? item.selectedColor.name : null,
        PAW: typeof item.PAW === 'number' ? item.PAW : 0, PCW: typeof item.PCW === 'number' ? item.PCW : 0,
        buyingPrice: typeof item.buyingPrice === 'number' ? item.buyingPrice : 0,
      })),
      subtotalAmount: calculateSubtotal(), discountAmount: calculateDiscount(),
      CustomerDeliveryCharge: chargeForApi, AdjustedCustomerDeliveryCharge: acdcValue,
      totalAmount: calculateTotal(), paymentMethod: selectedPaymentMethod,
      paymentDetails: (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed
        ? { trxId: bkashTrxId, phoneNo: bkashPhoneNo } : null,
      appliedCouponCode: appliedCoupon ? appliedCoupon.code : null, status: 'Processing', reviewAdded: false,
      clientId, totalPAW, totalPCW, totalRevenue, ActualTotalRevenue: actualTotalRevenue,
      deliveryOption, fbc, fbp, external_id: clientId, tracking_history: utmHistory,
    };
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/place_order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderSummary),
      });
      const data = await response.json();
      if (response.ok) {
        console.log('Order successfully placed:', data.message);
        if (data.access_token && data.user_info) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('user_info', JSON.stringify(data.user_info));
          if (typeof login === 'function') login(data.access_token, data.user_info);
          localStorage.setItem('just_logged_in', 'true');
        }
        if (appliedCoupon) await updateCouponUsage(appliedCoupon.code);
        setCart([]);
        navigate('/thank-you', { state: { orderSummary: data.order } });
      } else { setMainFormError(data.error || 'Failed to place order.'); }
    } catch (error) { console.error('Error placing order:', error); setMainFormError('Network error placing order.'); }
    finally { setIsOrderPlacing(false); }
  };

  useEffect(() => {
    const fetchDivisions = async () => {
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/division`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) setDivisions(data.data);
        else setError("Division data in unexpected format.");
      } catch (err) { setError(`Error loading divisions: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchDivisions();
  }, []);

  useEffect(() => {
    const fetchDistricts = async () => {
      if (!selectedDivisionId) { setDistricts([]); setSelectedDistrictId(''); setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/district/${selectedDivisionId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setDistricts(data.data); setSelectedDistrictId(''); setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); }
        else setError("District data in unexpected format.");
      } catch (err) { setError(`Error loading districts: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchDistricts();
  }, [selectedDivisionId]);

  useEffect(() => {
    const fetchUpazilas = async () => {
      if (!selectedDistrictId) { setUpazilas([]); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/upazilla/${selectedDistrictId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setUpazilas(data.data); setSelectedUpazilaId(''); setUnions([]); setSelectedUnionId(''); }
        else setError("Upazila data in unexpected format.");
      } catch (err) { setError(`Error loading upazilas: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchUpazilas();
  }, [selectedDistrictId]);

  useEffect(() => {
    const fetchUnions = async () => {
      if (!selectedUpazilaId) { setUnions([]); setSelectedUnionId(''); return; }
      setLoading(true); setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/union/${selectedUpazilaId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) { setUnions(data.data); setSelectedUnionId(''); }
        else setError("Union data in unexpected format.");
      } catch (err) { setError(`Error loading unions: ${err.message}`); }
      finally { setLoading(false); }
    };
    fetchUnions();
  }, [selectedUpazilaId]);

  const handleDivisionChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDivisionId(selectedId);
    const foundDivision = divisions.find(d => d.id === selectedId);
    setSelectedDivisionName(foundDivision ? foundDivision.bn_name : "");
  };

  const handleDistrictChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDistrictId(selectedId);
    const foundDistrict = districts.find(d => d.id === selectedId);
    if (foundDistrict) {
      setSelectedDistrictName(foundDistrict.bn_name);
      if (foundDistrict.bn_name === 'ঢাকা') setDeliveryOption('insideDhaka');
      else if (foundDistrict.bn_name !== '') setDeliveryOption('outsideDhaka');
    } else { setSelectedDistrictName(""); setDeliveryOption(''); }
  };

  const updateQuantity = (productId, newQuantity) => {
    const updatedItems = cartItems.map(item => item.id === productId ? { ...item, quantity: newQuantity } : item).filter(item => item.quantity > 0);
    navigate(location.pathname, { replace: true, state: { cartItems: updatedItems } });
  };

  const removeItem = (productId) => {
    navigate(location.pathname, { replace: true, state: { cartItems: cartItems.filter(item => item.id !== productId) } });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const nameVal = document.getElementById("name")?.value || "";
      const phoneVal = document.getElementById("phone")?.value || "";
      const addressVal = document.getElementById("address")?.value || "";
      setFormData(prev => ({ ...prev, name: nameVal, phone: phoneVal, address: addressVal }));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="checkout-root">

        {/* ── Floating Cart ── */}
        <div className="floating-cart">
          <FaShoppingCart className="floating-cart-icon" />
          <span className="floating-cart-label">Total</span>
          <span className="floating-cart-amount">৳{calculateTotal() + chargeForApi}</span>
        </div>

        {/* ── Header ── */}
        <div className="checkout-header">
          <FaSeedling style={{ color: '#F0C400', fontSize: '1.8rem' }} />
          <div>
            <div className="checkout-header-title">Secure Checkout</div>
            <div className="checkout-header-sub">Fresh & Organic — Farm to Your Door</div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="checkout-grid">

          {/* ── LEFT: Form ── */}
          <div>
            <form onSubmit={handleOrderSubmit} className="form-stack">

              {/* Step 1 — Shipping */}
              <div className="organic-card">
                <div className="organic-card-header">
                  <div className="step-circle">1</div>
                  <div>
                    <div className="section-label">Delivery Details</div>
                    <h2 className="organic-heading" style={{ fontSize: '1.25rem' }}>Shipping Address</h2>
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <span className="leaf-badge"><FaLeaf size={10} /> Required</span>
                  </div>
                </div>

                <div className="form-stack" style={{ gap: '16px' }}>

                  <div className="field-group">
                    <label className="organic-label" htmlFor="phone">📱 Mobile Number</label>
                    <input
                      type="number" autoComplete="off" id="phone" name="phone"
                      value={formData.phone} onChange={handleInputChange} onInput={handleInputChange}
                      required className="organic-input"
                      placeholder="01XXXXXXXXX"
                    />
                    {message && (
                      <p style={{ fontSize: '0.78rem', marginTop: '4px', color: isValid ? '#4F7E17' : '#CD0302', fontWeight: 600 }}>
                        {isValid ? '✓ ' : '✗ '}{message}
                      </p>
                    )}
                  </div>

                  <div className="field-group">
                    <label className="organic-label" htmlFor="name">👤 Full Name</label>
                    <input
                      type="text" id="name" name="name" value={formData.name}
                      autoComplete="off" onChange={handleInputChange} onInput={handleInputChange}
                      required className="organic-input" placeholder="Your full name"
                    />
                  </div>

                  <div className="two-col">
                    <div className="field-group">
                      <label className="organic-label" htmlFor="division-select">🗺 Division</label>
                      <div className="select-wrap">
                        <select id="division-select" className="organic-select"
                          value={selectedDivisionId} onChange={handleDivisionChange} disabled={loading}>
                          <option value="">Select Division</option>
                          {divisions.map(d => <option key={d.id} value={d.id}>{d.bn_name}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="field-group">
                      <label className="organic-label" htmlFor="district-select">📍 District</label>
                      <div className="select-wrap">
                        <select id="district-select" className="organic-select"
                          value={selectedDistrictId} onChange={handleDistrictChange}
                          disabled={!selectedDivisionId || loading}>
                          <option value="">Select District</option>
                          {districts.map(d => <option key={d.id} value={d.id}>{d.bn_name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="field-group">
                    <label className="organic-label" htmlFor="address">🏠 Full Address</label>
                    <textarea
                      id="address" name="address" value={formData.address}
                      onChange={handleInputChange} required autoComplete="off"
                      onInput={handleInputChange} rows="3" className="organic-textarea"
                      placeholder="House no., road, area..."
                    />
                    {addressMessage && (
                      <p style={{ fontSize: '0.78rem', color: '#F88702', marginTop: '4px', fontWeight: 600 }}>
                        ⚡ {addressMessage}
                      </p>
                    )}
                  </div>

                  <div className="field-group">
                    <label className="organic-label" htmlFor="notes">📝 Order Notes <span style={{ fontWeight: 400, color: '#aaa' }}>(Optional)</span></label>
                    <textarea
                      id="notes" name="notes" value={formData.notes}
                      onChange={handleInputChange} rows="2" className="organic-textarea"
                      placeholder="Any special instructions..."
                    />
                  </div>

                </div>
              </div>

              {/* Step 2 — Payment */}
              <div className="organic-card">
                <div className="organic-card-header">
                  <div className="step-circle" style={{ background: '#F88702' }}>2</div>
                  <div>
                    <div className="section-label">Choose Method</div>
                    <h2 className="organic-heading" style={{ fontSize: '1.25rem' }}>Payment Method</h2>
                  </div>
                </div>

                <div className="form-stack" style={{ gap: '12px' }}>

                  {/* COD */}
                  <label
                    className={`radio-card ${selectedPaymentMethod === 'cashOnDelivery' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('cashOnDelivery')}
                  >
                    <div className="radio-label">
                      <input type="radio" name="paymentMethod" value="cashOnDelivery"
                        checked={selectedPaymentMethod === 'cashOnDelivery'}
                        onChange={() => handlePaymentMethodChange('cashOnDelivery')} />
                      Cash on Delivery
                    </div>
                    <FaMoneyBillWave style={{ color: '#4F7E17', fontSize: '1.4rem' }} />
                  </label>

                  {/* bKash */}
                  <label
                    className={`radio-card ${selectedPaymentMethod === 'bkash' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('bkash')}
                  >
                    <div className="radio-label">
                      <input type="radio" name="paymentMethod" value="bkash"
                        checked={selectedPaymentMethod === 'bkash'}
                        onChange={() => handlePaymentMethodChange('bkash')} />
                      bKash
                    </div>
                    <img src="https://www.logo.wine/a/logo/BKash/BKash-bKash-Logo.wine.svg"
                      alt="bKash" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
                  </label>

                  {/* Nagad */}
                  <label
                    className={`radio-card ${selectedPaymentMethod === 'nagad' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('nagad')}
                  >
                    <div className="radio-label">
                      <input type="radio" name="paymentMethod" value="nagad"
                        checked={selectedPaymentMethod === 'nagad'}
                        onChange={() => handlePaymentMethodChange('nagad')} />
                      Nagad
                    </div>
                    <img src="https://www.logo.wine/a/logo/Nagad/Nagad-Logo.wine.svg"
                      alt="Nagad" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
                  </label>

                </div>

                {(selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed && (
                  <div className="payment-confirmed" style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#4F7E17', marginBottom: '6px' }}>
                      ✓ Payment Details Confirmed
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#555' }}>TrxID: <strong>{bkashTrxId}</strong></p>
                    <p style={{ fontSize: '0.85rem', color: '#555' }}>Phone: <strong>{bkashPhoneNo}</strong></p>
                  </div>
                )}
              </div>

              {/* Step 3 — Promo */}
              <div className="organic-card">
                <div className="organic-card-header" style={{ marginBottom: '16px' }}>
                  <div className="step-circle" style={{ background: '#F0C400', color: '#3d2b1f' }}>3</div>
                  <div>
                    <div className="section-label">Discount</div>
                    <h2 className="organic-heading" style={{ fontSize: '1.25rem' }}>Promo Code</h2>
                  </div>
                </div>
                <div className="coupon-row">
                  <input
                    type="text" id="coupon" name="coupon" placeholder="Enter coupon code"
                    value={couponCode} onChange={(e) => setCouponCode(e.target.value)}
                    className="organic-input"
                  />
                  <button type="button" className="btn-secondary" onClick={handleApplyCoupon}>Apply</button>
                </div>
                {couponError && <p style={{ fontSize: '0.82rem', color: '#CD0302', marginTop: '8px', fontWeight: 600 }}>✗ {couponError}</p>}
                {couponSuccess && <p style={{ fontSize: '0.82rem', color: '#4F7E17', marginTop: '8px', fontWeight: 600 }}>✓ {couponSuccess}</p>}
              </div>

              {mainFormError && (
                <div className="alert-error">✗ {mainFormError}</div>
              )}

              {/* Submit Button */}
              <button type="submit" disabled={!isOrderButtonEnabled || isOrderPlacing} className="btn-primary">
                {isOrderPlacing ? (
                  <><div className="spinner" /> Placing Order...</>
                ) : (
                  <><FaLeaf /> Place Your Order</>
                )}
              </button>

            </form>
          </div>

          {/* ── RIGHT: Order Summary ── */}
          <div>
            <div className="summary-card">
              <div style={{ marginBottom: '4px' }}>
                <div className="section-label">Your Basket</div>
                <h3 className="organic-heading" style={{ fontSize: '1.3rem' }}>Order Summary</h3>
              </div>

              <div style={{ borderBottom: '1px dashed #e8c070', margin: '16px 0' }} />

              {/* Items */}
              <div style={{ maxHeight: '340px', overflowY: 'auto', marginBottom: '16px', paddingRight: '4px' }}>
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <img src={item.image} alt={item.name} loading="lazy" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: '4px', lineHeight: 1.3 }}>{item.name}</p>
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p style={{ fontSize: '0.75rem', color: '#aaa', textDecoration: 'line-through' }}>৳{item.originalPrice}</p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                        <div className="qty-control">
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity === 1}>−</button>
                          <span className="qty-num">{item.quantity}</span>
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                        </div>
                        <button className="remove-btn" onClick={() => removeItem(item.id)}>Remove</button>
                      </div>
                      <p style={{ fontSize: '0.92rem', fontWeight: 700, color: '#CD0302', marginTop: '6px' }}>
                        ৳{item.price * item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div>
                <div className="price-row">
                  <span className="price-label">Subtotal</span>
                  <span className="price-val">৳{calculateSubtotal()}</span>
                </div>

                {appliedCoupon && (
                  <div className="price-row" style={{ marginTop: '8px' }}>
                    <span style={{ color: '#4F7E17', fontSize: '0.88rem' }}>Coupon ({appliedCoupon.code})</span>
                    <span style={{ color: '#4F7E17', fontWeight: 700 }}>−৳{calculateDiscount()}</span>
                  </div>
                )}

                <div className="price-row" style={{ marginTop: '8px' }}>
                  <span className="price-label">Delivery</span>
                  <span className={`price-val`} style={{ color: isFree ? '#4F7E17' : '#3d2b1f' }}>
                    {isFree ? <><span className="tag-free">FREE</span></> : deliveryOption ? displayCharge : '৳0'}
                  </span>
                </div>

                {calculateOverallSavings() > 0 && (
                  <div className="savings-strip" style={{ marginTop: '12px' }}>
                    <span style={{ fontWeight: 700, color: '#4F7E17', fontSize: '0.85rem' }}>🎉 Total Savings</span>
                    <span style={{ fontWeight: 800, color: '#4F7E17', fontSize: '0.92rem' }}>৳{calculateOverallSavings()}</span>
                  </div>
                )}

                <div className="price-total-row">
                  <span className="price-total-label">Order Total</span>
                  <span className="price-total-val">৳{calculateTotal() + chargeForApi}</span>
                </div>
              </div>

              {/* Trust badges */}
              <div style={{ marginTop: '20px', padding: '14px', background: 'rgba(79,126,23,0.05)', borderRadius: '10px', border: '1px dashed rgba(79,126,23,0.25)' }}>
                <p style={{ fontSize: '0.75rem', color: '#777', textAlign: 'center', lineHeight: 1.6 }}>
                  🌿 100% Organic &nbsp;|&nbsp; 🔒 Secure Order &nbsp;|&nbsp; 🚚 Fast Delivery
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* ── Payment Modal ── */}
        {showPaymentModal && (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && (
          <div className="modal-overlay">
            <div className="modal-box">
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
              <div style={{ marginBottom: '4px' }}>
                <div className="section-label">{selectedPaymentMethod === 'bkash' ? 'Mobile Banking' : 'Mobile Banking'}</div>
                <h3 className="organic-heading" style={{ fontSize: '1.35rem' }}>
                  {selectedPaymentMethod === 'bkash' ? '🔴 bKash' : '🟠 Nagad'} Payment
                </h3>
              </div>
              <p style={{ fontSize: '0.88rem', color: '#777', marginTop: '8px' }}>
                Send payment to the agent number below, then enter your transaction details.
              </p>
              <div className="agent-number-box">
                Agent: {selectedPaymentMethod === 'bkash' ? bKashAgentNumber : nagadAgentNumber}
              </div>
              <div className="field-group" style={{ marginBottom: '12px' }}>
                <label className="organic-label">Transaction ID</label>
                <input type="text" placeholder="e.g. 8H7J6K5L" value={bkashTrxId}
                  onChange={(e) => setBkashTrxId(e.target.value)} className="organic-input" />
              </div>
              <div className="field-group" style={{ marginBottom: '16px' }}>
                <label className="organic-label">Your Mobile Number</label>
                <input type="tel" placeholder="01XXXXXXXXX (11 digits)" value={bkashPhoneNo}
                  onChange={(e) => setBkashPhoneNo(e.target.value)} className="organic-input" />
              </div>
              {paymentModalError && (
                <p style={{ fontSize: '0.82rem', color: '#CD0302', marginBottom: '12px', fontWeight: 600 }}>✗ {paymentModalError}</p>
              )}
              <button className="btn-modal" onClick={handlePaySubmit}>✓ Confirm Payment</button>
            </div>
          </div>
        )}

        {/* ── Loading Overlay ── */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-box">
              <div className="spinner-green" />
              <span style={{ color: '#4F7E17', fontWeight: 700, fontSize: '0.95rem' }}>Loading...</span>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default CheckoutPage;